import { useEffect, useState } from 'react';

/** Keep native vertical pan and pinch zoom; handle horizontal touch pan on phones. */
export function useMobileTableScroll() {
	const [element, ref] = useState<HTMLDivElement | null>(null);
	useEffect(() => {
		if (!element) return;
		const mobile = window.matchMedia('(max-width: 767px), (max-width: 1023px) and (pointer: coarse)');
		const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
		let frame = 0;
		let suppressClick = false;
		let gesture: { id: number; x: number; y: number; left: number; horizontal: boolean;
			lastX: number; lastTime: number; velocity: number } | undefined;
		const stop = () => {
			cancelAnimationFrame(frame);
			frame = 0;
			gesture = undefined;
		};
		const down = (event: PointerEvent) => {
			stop();
			suppressClick = false;
			if (!mobile.matches || event.pointerType !== 'touch' || !event.isPrimary) return;
			if ((event.target as Element).closest('input, textarea, select, [contenteditable="true"]')) return;
			gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, left: element.scrollLeft,
				horizontal: false, lastX: event.clientX, lastTime: event.timeStamp, velocity: 0 };
		};
		const move = (event: PointerEvent) => {
			if (!gesture || event.pointerId !== gesture.id) return;
			const dx = event.clientX - gesture.x;
			const dy = event.clientY - gesture.y;
			if (!gesture.horizontal) {
				if (Math.max(Math.abs(dx), Math.abs(dy)) < 8) return;
				// A vertical gesture stays native for smooth scrolling and browser accessibility.
				if (Math.abs(dy) >= Math.abs(dx)) { gesture = undefined; return; }
				gesture.horizontal = true;
				suppressClick = true;
				element.setPointerCapture(event.pointerId);
			}
			if (event.cancelable) event.preventDefault();
			element.scrollLeft = gesture.left - dx;
			const elapsed = event.timeStamp - gesture.lastTime;
			if (elapsed > 0) gesture.velocity = Math.max(-1.5, Math.min(1.5, (gesture.lastX - event.clientX) / elapsed));
			gesture.lastX = event.clientX;
			gesture.lastTime = event.timeStamp;
		};
		const up = (event: PointerEvent) => {
			if (!gesture || event.pointerId !== gesture.id) return;
			const finished = gesture;
			gesture = undefined;
			if (element.hasPointerCapture(event.pointerId)) element.releasePointerCapture(event.pointerId);
			if (!finished.horizontal || reduceMotion.matches || event.timeStamp - finished.lastTime > 80) return;
			let velocity = finished.velocity;
			let lastTime = performance.now();
			const coast = (now: number) => {
				const elapsed = Math.min(now - lastTime, 32);
				lastTime = now;
				const previous = element.scrollLeft;
				element.scrollLeft += velocity * elapsed;
				velocity *= Math.pow(0.9, elapsed / 16);
				if (Math.abs(velocity) > 0.03 && Math.abs(element.scrollLeft - previous) > 0.1)
					frame = requestAnimationFrame(coast);
			};
			frame = requestAnimationFrame(coast);
		};
		const click = (event: MouseEvent) => {
			if (!suppressClick || event.detail === 0) return;
			suppressClick = false;
			event.preventDefault();
			event.stopPropagation();
		};
		// Table layout distributes spare width with small competitor sets.
		// Read the real first column width instead of assuming it always stays 32 px.
		const measure = () => {
			const cell = element.querySelector<HTMLTableCellElement>('.rates-date-table th');
			if (cell) element.style.setProperty('--rates-selection-width', `${cell.getBoundingClientRect().width}px`);
		};
		const resize = new ResizeObserver(measure);
		resize.observe(element);
		const content = new MutationObserver(measure);
		content.observe(element, { childList: true, subtree: true });
		measure();
		element.addEventListener('pointerdown', down);
		element.addEventListener('pointermove', move, { passive: false });
		element.addEventListener('pointerup', up);
		element.addEventListener('pointercancel', stop);
		element.addEventListener('click', click, true);
		mobile.addEventListener('change', stop);
		return () => {
			stop(); resize.disconnect(); content.disconnect();
			element.removeEventListener('pointerdown', down);
			element.removeEventListener('pointermove', move);
			element.removeEventListener('pointerup', up);
			element.removeEventListener('pointercancel', stop);
			element.removeEventListener('click', click, true);
			mobile.removeEventListener('change', stop);
		};
	}, [element]);
	return ref;
}
